import {COLORS} from "~/style/colors-ts";
import type {HealthScoreDomain, SupplierHealthScore} from "~/pages/supplier-360/types/Supplier360Types";

export type HealthTier = 'good' | 'med' | 'bad';

/** The design's display order (`supplier-signals.js` `DOMMAP`) — the API returns rows unordered. */
export const HEALTH_DOMAIN_ORDER: HealthScoreDomain[] = ['Financial', 'Operational', 'Compliance', 'Cybersecurity', 'ESG', 'Regional'];

/** Higher is healthier. Same cut-offs as the design's band: ≥75 low risk, ≥60 medium, else high. */
export function healthTier(score: number): HealthTier {
    if (score >= 75) return 'good';
    if (score >= 60) return 'med';
    return 'bad';
}

export const HEALTH_BAND_LABEL: Record<HealthTier, string> = {
    good: 'Low risk',
    med: 'Medium risk',
    bad: 'High risk',
};

export const HEALTH_TIER_COLOR: Record<HealthTier, {ink: string, tint: string, name: string}> = {
    good: {ink: COLORS.success.main, tint: COLORS.success[100], name: 'success'},
    med: {ink: COLORS.info.dark, tint: COLORS.mithraBlue[100], name: 'info'},
    bad: {ink: COLORS.warning.main, tint: COLORS.warning[100], name: 'warning'},
};

export function sortHealthDomains(rows: SupplierHealthScore[]): SupplierHealthScore[] {
    return [...rows].sort((a, b) => HEALTH_DOMAIN_ORDER.indexOf(a.domain) - HEALTH_DOMAIN_ORDER.indexOf(b.domain));
}

/**
 * The overall score is not on `health_score` (§4.11). The mean of the 6 domains is the fallback
 * when `kpi_summary.risk_score` — the same average, computed server-side — isn't to hand.
 */
export function compositeHealthScore(rows: SupplierHealthScore[]): number {
    if (!rows.length) return 0;
    return Math.round(rows.reduce((sum, r) => sum + r.score, 0) / rows.length);
}

/** `+3`, `−2`, `±0` — the design's delta tag wording, rounded to whole points. */
export function formatHealthDelta(delta: number): string {
    const rounded = Math.round(delta);
    if (rounded > 0) return `+${rounded}`;
    if (rounded < 0) return `−${Math.abs(rounded)}`;
    return '±0';
}
